import React, { useCallback, useState, useEffect } from "react";
import { BusinessIvaProRataForm } from "../../../../../../components/form/business/ivaprorata";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetAdditionalBusinessDetails } from "../../../../../../hooks/api/business/useGetAdditionalBusinessDetails";
import { usePatchAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePatchAdditionalBusinessDetails";
import { usePostAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePostAdditionalBusinessDetails";
import { useDelete } from "../../../../../../hooks/api/delete/useDelete";
import WsSnackbar, { WsSnackbarTypes } from "../../../../../../components/ws-snackbar";
import { Feedback } from "../../../../../../@types/enums/general/Feedback";
import { Alert, Stack, Link } from "@vapor/react-material";
import { Trans, useTranslation } from "@onefront/react-sdk";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

export const BusinessEditIvaProRata = ({ isEditing, setIsEditing, setLoadingSection, loading }) => {
    const { t } = useTranslation();
    const { workspaceId, businessId } = useParams();
    const [bodyRequest, setBodyRequest] = useState<any>({
        ivaProRataHandled: false
    });
    const [items, setItems] = useState<any>(null);
    const [itemToSave, setItemToSave] = useState<any>({});
    const [isEditingItem, setIsEditingItem] = useState<any>(null);
    const [reload, setReload] = useState<any>(true);
    const [feedback, setFeedback] = useState<any>(null);
    const [searchParams] = useSearchParams();
    const editing = searchParams.get("editing");

    const {
        data: ivaProRatas,
        loading: loadingIvaProRatas,
        fetch: getIvaProRatas
    } = useGetAdditionalBusinessDetails(workspaceId, businessId, "iva-pro-rata");

    const { loading: loadingPatch, fetch: patchIvaProRata } = usePatchAdditionalBusinessDetails(
        workspaceId,
        businessId,
        "iva-pro-rata"
    );

    const { loading: loadingPost, fetch: postIvaProRata } = usePostAdditionalBusinessDetails(
        workspaceId,
        businessId,
        "iva-pro-rata"
    );

    const { loading: loadingDelete, fetch: deleteIvaProRata } = useDelete(workspaceId);

    const dateYear = new Date().getFullYear();

    const handleChange = useCallback(
        (value, field) => {
            setBodyRequest({ ...bodyRequest, [field]: value });
        },
        [bodyRequest]
    );

    const isLoading = useCallback(() => {
        return loading || loadingIvaProRatas || loadingPatch || loadingPost || loadingDelete;
    }, [loading, loadingIvaProRatas, loadingPatch, loadingPost, loadingDelete]);

    const onForward = useCallback(() => {
        const { id, idIvaProRata, proRataNonDeducibilityRate, ...payload } = items[0];

        if (bodyRequest?.ivaProRataHandled) {
            if (idIvaProRata) {
                patchIvaProRata({
                    url: `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/business/${businessId}/iva-pro-rata/${idIvaProRata}`,
                    data: {
                        ...payload,
                        proRataNonDeducibilityRate: parseInt(proRataNonDeducibilityRate)
                    }
                })
                    .then(res => {
                        setReload(true);
                        setIsEditing(false);
                        getIvaProRatas?.();
                        setFeedback(Feedback.SUCCESS);
                    })
                    .catch(err => {
                        setFeedback(Feedback.ERROR);
                    });
            } else {
                postIvaProRata({
                    data: {
                        ...payload,
                        proRataNonDeducibilityRate: parseInt(proRataNonDeducibilityRate)
                    }
                })
                    .then(res => {
                        setReload(true);
                        setIsEditing(false);
                        getIvaProRatas?.();
                        setFeedback(Feedback.SUCCESS);
                    })
                    .catch(err => {
                        setFeedback(Feedback.ERROR);
                    });
            }
        } else {
            if (idIvaProRata) {
                deleteIvaProRata({
                    url: `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/business/${businessId}/iva-pro-rata/${idIvaProRata}`
                })
                    .then(res => {
                        setIsEditing(false);
                        setReload(true);
                        getIvaProRatas?.();
                        setFeedback(Feedback.SUCCESS);
                    })
                    .catch(err => {
                        setFeedback(Feedback.ERROR);
                    });
            } else {
                setIsEditing(false);
            }
        }
    }, [
        bodyRequest,
        businessId,
        workspaceId,
        patchIvaProRata,
        deleteIvaProRata,
        postIvaProRata,
        items,
        setIsEditing,
        getIvaProRatas
    ]);

    const onBack = useCallback(() => {
        setIsEditing(false);
        setBodyRequest({
            ivaProRataHandled: ivaProRatas?.find(item => item?.proRataYear === dateYear)?.length > 0
        });
    }, [dateYear, ivaProRatas, setIsEditing]);

    useEffect(() => {
        if (reload && !loadingIvaProRatas && ivaProRatas) {
            const defaultItem = {
                id: dateYear,
                idIvaProRata: null,
                proRataManagementType: "NON_DEDUCTIBLE_VAT_ON_COSTS",
                proRataYear: dateYear,
                proRataNonDeducibilityRate: "100"
            };

            const ivaProRataOfCurrentYear = ivaProRatas?.find(item => item?.proRataYear === dateYear);

            if (ivaProRataOfCurrentYear) {
                setBodyRequest(prev => ({ ...prev, ivaProRataHandled: true }));
                setItems([
                    {
                        ...ivaProRataOfCurrentYear,
                        id: ivaProRataOfCurrentYear?.proRataYear,
                        idIvaProRata: ivaProRataOfCurrentYear?.id,
                        proRataNonDeducibilityRate: `${ivaProRataOfCurrentYear?.proRataNonDeducibilityRate}%`
                    }
                ]);

                setIsEditing(false);
                setReload(false);
            } else {
                setItems([defaultItem]);
                setItemToSave(defaultItem);
                setIsEditing(false);
            }
        }
    }, [reload, ivaProRatas, dateYear, setIsEditing, loadingIvaProRatas]);

    useEffect(() => {
        setLoadingSection(loadingIvaProRatas || loading);
    }, [loadingIvaProRatas, loading, setLoadingSection]);

    useEffect(() => {
        if (!loading || !loadingIvaProRatas) {
            if (editing) {
                setIsEditing(editing);
                handleChange(true, "ivaProRataHandled");
                setIsEditingItem(dateYear);
                searchParams.delete("editing");
            }
        }
    }, [loading, loadingIvaProRatas, editing, setIsEditing, searchParams, dateYear, handleChange, items]);

    return (
        <Stack position="relative">
            {feedback && (
                <WsSnackbar
                    position={"absolute"}
                    sx={{
                        right: "0 !important",
                        top: "-4.5rem !important",
                        width: 700
                    }}
                    type={feedback === Feedback.ERROR ? WsSnackbarTypes.error : WsSnackbarTypes.success}
                    title={t(`business.ivaprorata.title`)}
                    message={t(`feedback.business.edit.${feedback}`)}
                    horizontal="right"
                    handleClose={() => setFeedback(null)}
                />
            )}

            <Stack width={768}>
                {!isLoading() && !isEditing && !bodyRequest?.ivaProRataHandled && (
                    <Alert
                        icon={<FontAwesomeIcon icon={faExclamationTriangle} fontSize={22} />}
                        severity="warning"
                        variant="outlined"
                        sx={{
                            mb: 4
                        }}
                    >
                        <ExtendedTypography variant="bodyLarge">
                            <Trans
                                i18nKey="business.ivaprorata.warning"
                                components={{
                                    b: <strong />
                                }}
                            />
                        </ExtendedTypography>
                        <Link
                            sx={{
                                ml: 1
                            }}
                            onClick={() => {
                                handleChange(true, "ivaProRataHandled");
                                setIsEditing(true);
                                const ivaProRataOfYear = items?.find(item => item?.proRataYear === dateYear);
                                setItemToSave({
                                    id: dateYear,
                                    idIvaProRata: ivaProRataOfYear?.idIvaProRata || null,
                                    proRataManagementType: "NON_DEDUCTIBLE_VAT_ON_COSTS",
                                    proRataYear: dateYear,
                                    proRataNonDeducibilityRate: "100"
                                });
                                setIsEditingItem(dateYear);
                            }}
                        >
                            <ExtendedTypography variant="bodyLarge" color="#0090d1">
                                <Trans
                                    i18nKey="business.ivaprorata.warningLink"
                                    values={{
                                        year: dateYear
                                    }}
                                />
                            </ExtendedTypography>
                        </Link>
                    </Alert>
                )}
                <BusinessIvaProRataForm
                    items={items}
                    setItems={setItems}
                    businessId={businessId}
                    pastIvaProRata={ivaProRatas?.filter(item => item?.proRataYear !== dateYear) || []}
                    bodyRequest={bodyRequest}
                    handleChange={handleChange}
                    onForward={onForward}
                    onBack={onBack}
                    itemToSave={itemToSave}
                    setItemToSave={setItemToSave}
                    onDelete={() => {}}
                    isEditingItem={isEditingItem}
                    setIsEditingItem={setIsEditingItem}
                    isEditing={isEditing}
                    loading={isLoading()}
                    isNewBusiness={false}
                />
            </Stack>
        </Stack>
    );
};
